import React, { useMemo } from "react";
import parseAndFormatDate from "./utils/ParseAndFormatDate";
import { currencyWithCommas } from "./utils/NumberFormatter";
import validStateBadgeClass from "./utils/validStateBadgeClass";

export const useClaimsDataColumns = (claimType) => {
  const claimKey = claimType == "medical" ? "medical_claim" : "pharmacy_claim";

  const formatAddress = (pharmacy) => {
    if (!pharmacy) return "";

    const address2 = pharmacy.pharmacy_address2 ? ` ${pharmacy.pharmacy_address2} ` : "";
    const address3 = pharmacy.pharmacy_address3 ? ` ${pharmacy.pharmacy_address3} ` : "";
    return `${pharmacy.pharmacy_address1 + address2 + address3} ${pharmacy.pharmacy_city} ${
      pharmacy.pharmacy_state
    } ${pharmacy.pharmacy_zip}`;
  };

  const pharmacyColumns = useMemo(
    () => [
      {
        Header: "Results",
        accessor: `${claimKey}.status`,
        Cell: (props) => (
          <div
            className={
              "badge text-uppercase fw-normal bg-" +
              validStateBadgeClass(props.row.original[claimKey]?.status)
            }
          >
            {props.row.original[claimKey]?.status}
          </div>
        ),
      },
      {
        Header: "Issues",
        accessor: `${claimKey}.csv_codes`,
      },
      {
        Header: "ID 340B",
        accessor: `${claimKey}.id_340b`,
        filter: "fuzzyText",
        Cell: (row) => <a href={`/entities/340bid/${row.value}`}>{row.value}</a>,
      },
      {
        Header: "Date",
        accessor: `${claimKey}.created_at`,
        filter: "fuzzyText",
        Cell: (props) => (
          <small className="text-muted">
            {parseAndFormatDate(props.row.original[claimKey]?.created_at)}
          </small>
        ),
      },
      {
        Header: "Manufacturer",
        accessor: `${claimKey}.manufacturer`,
        filter: "fuzzyText",
        Cell: (props) => props.row.original[claimKey]?.manufacturer,
      },
      {
        Header: "NDC",
        accessor: `${claimKey}.ndc`,
        filter: "fuzzyText",
        Cell: (props) => <small className="text-muted">{props.row.original[claimKey]?.ndc}</small>,
      },
      {
        Header: "Quantity",
        accessor: `${claimKey}.quantity`,
      },
      {
        Header: "Prescriber Id Qualifier",
        accessor: `${claimKey}.prescriber_id_qualifier`,
      },
      {
        Header: "Prescriber Id",
        accessor: `${claimKey}.prescriber_id`,
        Cell: (props) => (
          <small className="text-muted">{props.row.original[claimKey]?.prescriber_id}</small>
        ),
      },
      {
        Header: "Service Provider Id Qualifier",
        accessor: `${claimKey}.service_provider_id_qualifier`,
      },
      {
        Header: "Service Provider Id",
        accessor: `${claimKey}.service_provider_id`,
      },
      {
        Header: "Contracted Entity Id",
        accessor: `${claimKey}.contracted_entity_id`,
      },
      {
        Header: "Staging",
        accessor: `${claimKey}.staging`,
      },
      {
        Header: "Network",
        accessor: `${claimKey}.network`,
        Cell: (props) => (
          <div className="badge bg-secondary text-uppercase">
            {props.row.original[claimKey]?.network}
          </div>
        ),
      },
      {
        Header: "DEA",
        accessor: `${claimKey}.dea`,
      },
      {
        Header: "Pharmacy Id",
        accessor: `${claimKey}.pharmacy_id`,
        Cell: (props) => (
          <small className="text-muted">{props.row.original[claimKey]?.pharmacy_id}</small>
        ),
      },
      {
        Header: "Pharmacy Name",
        accessor: "pharmacy.pharmacy_name",
      },
      {
        Header: "Pharmacy Address",
        accessor: "pharmacy.pharmacy_address",
        Cell: (props) => {
          return formatAddress(props.row.original.pharmacy);
        },
      },
      {
        Header: "Chain Name",
        accessor: `${claimKey}.chain_name`,
        Cell: (props) => <b>{props.row.original[claimKey]?.chain_name}</b>,
      },
      {
        Header: "Product Name",
        accessor: `${claimKey}.product_name`,
      },
      {
        Header: "WAC Price",
        accessor: `${claimKey}.wac_price`,
        Cell: (props) => currencyWithCommas(props.row.original[claimKey]?.wac_price),
      },
      {
        Header: "In-House Pharmacy IDs",
        accessor: `${claimKey}.in_house_pharmacy_ids`,
        Cell: (props) => (
          <small className="text-muted">
            {props.row.original[claimKey]?.in_house_pharmacy_ids?.join(", ")}
          </small>
        ),
      },
      {
        Header: "Payer BIN",
        accessor: `${claimKey}.payer_bin`,
      },
      {
        Header: "Payer PCN",
        accessor: `${claimKey}.payer_pcn`,
      },
      {
        Header: "Ship To Date",
        accessor: `${claimKey}.ship_to_date`,
        Cell: (props) => (
          <small className="text-muted">
            {parseAndFormatDate(props.row.original[claimKey]?.ship_to_date)}
          </small>
        ),
      },
      {
        Header: "Ship To Location",
        accessor: `${claimKey}.ship_to_location`,
      },
      {
        Header: "Account 340B",
        accessor: `${claimKey}.account_340b`,
      },
      {
        Header: "Fill Number",
        accessor: `${claimKey}.fill_number`,
      },
      {
        Header: "Submitted By",
        accessor: `${claimKey}.submitted_by`,
        filter: "fuzzyText",
        Cell: (props) => (
          <small className="text-muted">{props.row.original[claimKey]?.submitted_by}</small>
        ),
      },
      {
        Header: "Wholesaler Invoice Number",
        accessor: `${claimKey}.wholesaler_invoice_number`,
      },
    ],
    []
  );

  const medicalColumns = useMemo(
    () => [
      {
        Header: "Results",
        accessor: `${claimKey}.vanity_status`,
        Cell: (props) => (
          <div
            className={
              "badge text-uppercase fw-normal bg-" +
              validStateBadgeClass(props.row.original[claimKey]?.vanity_status)
            }
          >
            {props.row.original[claimKey]?.vanity_status}
          </div>
        ),
      },
      {
        Header: "Issues",
        accessor: `${claimKey}.csv_codes`,
      },
      {
        Header: "ID 340B",
        accessor: `${claimKey}.id_340b`,
        filter: "fuzzyText",
        Cell: (row) => <a href={`/entities/340bid/${row.value}`}>{row.value}</a>,
      },
      {
        Header: "ID 340B Raw",
        accessor: `${claimKey}.id_340b_raw`,
        filter: "fuzzyText",
        Cell: (row) => <a href={`/entities/340bid/${row.value}`}>{row.value}</a>,
      },
      {
        Header: "Date",
        accessor: `${claimKey}.created_at`,
        filter: "fuzzyText",
        Cell: (props) => (
          <small className="text-muted">
            {parseAndFormatDate(props.row.original[claimKey]?.created_at)}
          </small>
        ),
      },
      {
        Header: "Manufacturer",
        accessor: `${claimKey}.manufacturer`,
        filter: "fuzzyText",
        Cell: (props) => props.row.original[claimKey]?.manufacturer,
      },
      {
        Header: "NDC",
        accessor: `${claimKey}.ndc`,
        filter: "fuzzyText",
        Cell: (props) => <small className="text-muted">{props.row.original[claimKey]?.ndc}</small>,
      },
      {
        Header: "Quantity",
        accessor: `${claimKey}.quantity`,
      },
      {
        Header: "Unit Quantity",
        accessor: `${claimKey}.unit_quantity`,
      },
      {
        Header: "Service Provider Id",
        accessor: `${claimKey}.service_provider_id`,
      },
      {
        Header: "Staging",
        accessor: `${claimKey}.staging`,
        Cell: (row) => (
          <span className="badge bg-secondary text-uppercase">
            {row.value == true ? "TRUE" : "FALSE"}
          </span>
        ),
      },
      {
        Header: "Network",
        accessor: `${claimKey}.network`,
        filter: "fuzzyText",
        Cell: (props) => (
          <div className="badge bg-secondary text-uppercase">
            {props.row.original[claimKey]?.network}
          </div>
        ),
      },
      {
        Header: "Pharmacy Id",
        accessor: `${claimKey}.pharmacy_id`,
      },
      {
        Header: "Chain Name",
        accessor: `${claimKey}.chain_name`,
      },
      {
        Header: "Product Name",
        accessor: `${claimKey}.product_name`,
      },
      {
        Header: "In-House Pharmacy IDs",
        accessor: `${claimKey}.in_house_pharmacy_ids`,
        Cell: (props) => (
          <small className="text-muted">
            {props.row.original[claimKey]?.in_house_pharmacy_ids?.join(", ")}
          </small>
        ),
      },
      {
        Header: "Health Plan ID",
        accessor: `${claimKey}.health_plan_id`,
        filter: "fuzzyText",
      },
      {
        Header: "Health Plan Name",
        accessor: `${claimKey}.health_plan_name`,
        filter: "fuzzyText",
      },
      {
        Header: "Unit of Measure",
        accessor: `${claimKey}.unit_of_measure`,
        filter: "fuzzyText",
      },
      {
        Header: "Date of Service",
        accessor: `${claimKey}.date_of_service`,
        filter: "fuzzyText",
        Cell: (props) => (
          <small className="text-muted">
            {parseAndFormatDate(props.row.original[claimKey]?.date_of_service)}
          </small>
        ),
      },
      {
        Header: "Rendering Physician ID",
        accessor: `${claimKey}.rendering_physician_id`,
      },
      {
        Header: "Claim Line Number",
        accessor: `${claimKey}.claim_line_number`,
      },
      {
        Header: "HCPCS Code",
        accessor: `${claimKey}.hcpcs_code`,
      },
      {
        Header: "HCPCS Code Mod 1",
        accessor: `${claimKey}.hcpcs_code_modifier_1`,
      },
      {
        Header: "HCPCS Code Mod 2",
        accessor: `${claimKey}.hcpcs_code_modifier_2`,
      },
      {
        Header: "HCPCS Code Mod 3",
        accessor: `${claimKey}.hcpcs_code_modifier_3`,
      },
      {
        Header: "HCPCS Code Mod 4",
        accessor: `${claimKey}.hcpcs_code_modifier_4`,
      },
      {
        Header: "Wholesaler Invoice Number",
        accessor: `${claimKey}.wholesaler_invoice_number`,
      },
    ],
    []
  );

  return claimType == "medical" ? medicalColumns : pharmacyColumns;
};
